import { layout } from "@tokens/index";

export type PopoverPlacement = "bottom-start" | "bottom-end" | "top-start" | "top-end";

export type PanelSide = "top" | "bottom";

export interface PanelPosition {
    readonly top: number;
    readonly left: number;
    /** Set only when the panel matches the anchor width. */
    readonly width: number | undefined;
    /** Vertical room between the anchor and the viewport edge on the chosen side. */
    readonly availableHeight: number;
    /** The side of the anchor the panel landed on, after any flip. */
    readonly side: PanelSide;
}

/**
 * `transform-origin` so a scale-in grows out of the anchor edge: the panel's
 * top edge when it hangs below the anchor, its bottom edge when it sits
 * above; the start or end corner per the placement.
 */
export function transformOriginFor(placement: PopoverPlacement, side?: PanelSide): string {
    const vertical = side ?? (placement.startsWith("top") ? "top" : "bottom");
    const y = vertical === "bottom" ? "top" : "bottom";
    const x = placement.endsWith("end") ? "right" : "left";
    return `${x} ${y}`;
}

export interface AnchorRect {
    readonly top: number;
    readonly left: number;
    readonly right: number;
    readonly bottom: number;
    readonly width: number;
    readonly height: number;
}

export interface PanelSize {
    readonly width: number;
    readonly height: number;
}

export interface ViewportSize {
    readonly width: number;
    readonly height: number;
}

/** Gap between the anchor edge and the panel in px. */
export const ANCHOR_OFFSET = 4;

/**
 * Place a panel next to its anchor. Flip to the other vertical side when the
 * preferred side lacks room and the other side has more. Shift horizontally
 * to keep `layout.viewportGutter` from both edges.
 */
export function computePanelPosition(
    anchor: AnchorRect,
    panel: PanelSize,
    placement: PopoverPlacement,
    viewport: ViewportSize,
    matchAnchorWidth: boolean,
): PanelPosition {
    const gutter = layout.viewportGutter;
    const spaceBelow = viewport.height - anchor.bottom - gutter - ANCHOR_OFFSET;
    const spaceAbove = anchor.top - gutter - ANCHOR_OFFSET;

    let vertical: "top" | "bottom" = placement.startsWith("top") ? "top" : "bottom";
    if (vertical === "bottom" && panel.height > spaceBelow && spaceAbove > spaceBelow) {
        vertical = "top";
    } else if (vertical === "top" && panel.height > spaceAbove && spaceBelow >= spaceAbove) {
        vertical = "bottom";
    }

    const availableHeight = Math.max(0, vertical === "bottom" ? spaceBelow : spaceAbove);
    const renderedHeight = Math.min(panel.height, availableHeight);
    const top =
        vertical === "bottom"
            ? anchor.bottom + ANCHOR_OFFSET
            : Math.max(gutter, anchor.top - ANCHOR_OFFSET - renderedHeight);

    const width = matchAnchorWidth ? anchor.width : panel.width;
    const rawLeft = placement.endsWith("end") ? anchor.right - width : anchor.left;
    const maxLeft = Math.max(gutter, viewport.width - gutter - width);
    const left = Math.min(Math.max(rawLeft, gutter), maxLeft);

    return {
        top,
        left,
        width: matchAnchorWidth ? anchor.width : undefined,
        availableHeight,
        side: vertical,
    };
}

/**
 * Place the panel from the anchor before the panel itself has a size. Uses
 * `minWidth` (or the anchor width) and a 1px height so the preferred side
 * wins unless that side has no room. The caller replaces this with a
 * measured `computePanelPosition` once the panel is in the tree.
 */
export function seedPanelPosition(
    anchor: AnchorRect,
    placement: PopoverPlacement,
    viewport: ViewportSize,
    matchAnchorWidth: boolean,
    minWidth?: number,
): PanelPosition {
    const width = matchAnchorWidth ? anchor.width : (minWidth ?? 180);
    return computePanelPosition(
        anchor,
        { width, height: 1 },
        placement,
        viewport,
        matchAnchorWidth,
    );
}
